import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useNavigate } from 'react-router-dom';
import { schedulingRulesSchema, SchedulingRulesFormValues } from '../../lib/validations/teamSchema';
import { useTeamStore } from '../../lib/store/teamStore';
import Alert from '../common/Alert';
import Button from '../common/Button';
import Card from '../common/Card';
import Input from '../common/Input';

export const TeamSchedulingRulesForm = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { currentTeam, isLoading, error, setCurrentTeam, updateTeam } = useTeamStore();
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<SchedulingRulesFormValues>({
    resolver: zodResolver(schedulingRulesSchema),
    defaultValues: {
      scheduling_period_days: 28,
      max_consecutive_shifts: 5,
      min_rest_hours: 10
    }
  });
  
  useEffect(() => {
    if (teamId) {
      setCurrentTeam(teamId);
    }
  }, [teamId, setCurrentTeam]);
  
  useEffect(() => {
    if (currentTeam) {
      reset({
        scheduling_period_days: currentTeam.scheduling_period_days,
        max_consecutive_shifts: currentTeam.max_consecutive_shifts,
        min_rest_hours: currentTeam.min_rest_hours
      });
    }
  }, [currentTeam, reset]);
  
  const onSubmit = async (data: SchedulingRulesFormValues) => {
    if (!teamId) return;
    
    setIsSubmitting(true);
    setUpdateError(null);
    setSuccessMessage(null);
    
    try {
      await updateTeam(teamId, data);
      setSuccessMessage('Scheduling rules updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update scheduling rules. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }
  
  if (!currentTeam) {
    return (
      <Alert variant="error" className="mb-4">
        Team not found
      </Alert>
    );
  }
  
  return (
    <Card className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-6">Scheduling Rules</h2>
      
      {updateError && (
        <Alert variant="error" className="mb-4">
          {updateError}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <Input
            type="number"
            label="Scheduling Period (Days)"
            {...register('scheduling_period_days', { valueAsNumber: true })}
            error={errors.scheduling_period_days?.message}
            helperText="The number of days in each scheduling period"
            required
          />
          
          <Input
            type="number"
            label="Max Consecutive Shifts"
            {...register('max_consecutive_shifts', { valueAsNumber: true })}
            error={errors.max_consecutive_shifts?.message}
            helperText="Maximum number of shifts a provider can work consecutively"
            required
          />
          
          <Input
            type="number"
            label="Minimum Rest Hours"
            {...register('min_rest_hours', { valueAsNumber: true })}
            error={errors.min_rest_hours?.message}
            helperText="Minimum number of hours between shifts for a provider"
            required
          />
        </div>
        
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/teams/${teamId}/settings`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Save Rules
          </Button>
        </div>
      </form>
    </Card>
  );
}; 